// ABOUTME: The reel's bottom composition — keyboard and mic flanking the CC bar from OUTSIDE it.
// ABOUTME: Figma nodes 45:1197 (keyboard) / 45:1146 (bar) / 45:1211 (mic); see SPEC-26 R.2.

import React, { useEffect, useRef } from 'react';
import { Camera, Square } from 'lucide-react';
import { RAIL_UNIT, CAPTION_BAR_UNIT, u } from './railGeometry';
import CaptionOverlay, { BAR_W_PCT, BAR_H_PCT, BAR_BOTTOM_PCT } from '../Projectionist/overlays/CaptionOverlay';
import VoiceDock, { dockSlotWidth } from '../VoiceFirst/VoiceDock/VoiceDock'; // SPEC-82
import { buildDockControls } from '../VoiceFirst/VoiceDock/RevealedControls'; // SPEC-82 D-10

// Flanking icons — 120x117.33 frames whose glyph artwork is smaller than the frame, so the frame is
// the hit target and the glyph is centred in it. They sit OUTSIDE the bar with a 26.67px gutter
// (keyboard's right edge 823 vs the bar's left edge 850.67). Phase 4 had them inside the bar's own
// composition, which is not where the design puts them (R.3 defect 6).
// Both exported so the spacing test asserts the authored value rather than a copy of it that could
// silently drift — the same reason MobileFooter exports MOBILE_FIELD_FONT_SIZE. SpecSheetOverlay
// reads them too: the PDP pill hangs off this flank's left edge, which is the leftmost column of
// the whole bottom composition.
export const ICON_BOX = 120;
export const ICON_GUTTER = 26.667;
const KEYBOARD_GLYPH = { src: '/reel-chrome/icon-keyboard.svg', w: 72, h: 49.592 };
const MIC_GLYPH = { src: '/reel-chrome/icon-mic.svg', w: 46, h: 73.459 };
const MIC_GLYPH_OFF = { src: '/reel-chrome/icon-mic-off.svg', w: 72.119, h: 75.346 };
const CAPTION_FONT = 37.333;
const CAPTION_LINE_HEIGHT = 1.15;
// The bar's height is fixed (BAR_H_PCT), so a growing field can't push it taller — matching the
// agent/guest captions' own 2-line clamp (see CaptionOverlay's ScrollingCaption) keeps a long typed
// message readable (wraps, auto-scrolls to the caret) without ever breaking the bar's authored size.
const INPUT_VISIBLE_LINES = 2;
const INPUT_MAX_HEIGHT = CAPTION_FONT * CAPTION_LINE_HEIGHT * INPUT_VISIBLE_LINES;

// `disabled` is separate from `dimmed` on purpose: `dimmed` is how this chrome says "off" (the muted
// mic, the closed keyboard) and those controls are still very much pressable. Only the stop button
// is ever genuinely inert — there is nothing to stop — so it passes both.
function GlyphButton({ glyph, label, title, onClick, dimmed = false, disabled = false, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title || label}
      onClick={onClick}
      disabled={disabled}
      className={`relative flex items-center justify-center bg-reel-chrome-panel backdrop-blur-reel-chrome rounded-reel-iconButton hover:bg-white/10 transition-colors ${dimmed ? 'opacity-40' : 'opacity-100'}`}
      style={{ width: u(ICON_BOX), height: u(ICON_BOX) }}
    >
      {glyph ? (
        <img src={glyph.src} alt="" aria-hidden="true" style={{ width: u(glyph.w), height: u(glyph.h) }} />
      ) : children}
    </button>
  );
}

/**
 * The bottom chrome for reel mode. The bar is absolutely centred and the flanks are positioned
 * against its edges, so an uneven number of controls on either side can never push it off centre.
 *
 * One documented departure from the Figma frames, deliberate:
 *  - The (tenant-flagged) camera button has no slot in the design, but it is a working feature and
 *    rides alongside the keyboard rather than being dropped silently. See SPEC-26 R.6 OQ-R5.
 *    The CC toggle button that Phase 4 added here has been removed — the design has no control for
 *    it, and `ccEnabled` is driven entirely by App.js (on for reel mode) instead.
 *  - The centred particle cluster (node 45:2283) is the app's existing ParticlePill/Visualizer,
 *    passed in as `visualizer` — the design's cluster is a dot field, which is what it already is.
 */
function ReelFooter({
  ccEnabled,
  isKeyboardOpen, onOpenKeyboard, onCloseKeyboard,
  textInput, onTextInputChange, onSubmitText,
  isMicOn, onToggleMic,
  visualizer = null,
  agentState,
  showCamera = false, onCamera,
  showStop = false, canStop = false, onStopAgent,
  voiceFirst = null, // SPEC-82: null (every flag-off tenant) => the markup below is exactly today's
}) {
  const textareaRef = useRef(null);
  useEffect(() => {
    const el = textareaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [textInput]);

  const input = isKeyboardOpen ? (
    <form onSubmit={onSubmitText} className="flex items-center gap-2 w-full pointer-events-auto">
      <textarea
        ref={textareaRef}
        rows={1}
        value={textInput}
        onChange={(e) => onTextInputChange(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends, same as the old single-line field; Shift+Enter still breaks the line.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmitText(e);
          }
        }}
        placeholder="Type a message..."
        // Distinct from the keyboard button's "Type a message" — two controls sharing one
        // accessible name is ambiguous for screen readers, and unresolvable for tests.
        aria-label="Message"
        autoFocus
        disabled={agentState === 'thinking'}
        className="flex-1 min-w-0 resize-none overflow-y-hidden bg-transparent border-none focus:outline-none text-reel-chrome-textPrimary placeholder:text-white/40 font-reel-medium"
        style={{ fontSize: u(CAPTION_FONT), lineHeight: CAPTION_LINE_HEIGHT, maxHeight: u(INPUT_MAX_HEIGHT) }}
      />
    </form>
  ) : null;

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        '--u': RAIL_UNIT,
        bottom: `${BAR_BOTTOM_PCT}%`,
        left: '50%',
        transform: 'translateX(-50%)',
        width: `${BAR_W_PCT}%`,
        height: `${BAR_H_PCT}%`,
      }}
    >
      {/* Left flank — outside the bar. `pointerEvents` is set inline rather than via Tailwind's
          `pointer-events-auto`, because OverlayPortal disables pointer events with an INLINE
          style: a class cannot win against that on the parent, so the controls would be dead
          anywhere the compiled stylesheet is absent or loads late. */}
      {/* The flank's INTERNAL pitch is the same authored 26.667 gutter that separates it from the
          bar, so two adjacent glyphs read as two controls rather than one wide slab. Set as a `gap`
          on the row rather than a margin on any one button: the row grows with the tenant-flagged
          camera and the keyboard-mode stop button, and a gap paces all of them. It cannot move the
          flank relative to the bar either — `right` still anchors the row's right edge, and the
          rightmost child's right edge is the row's. */}
      {!voiceFirst?.hideFlanks && (
      <div
        className="absolute top-1/2 -translate-y-1/2 flex items-center"
        style={{ right: `calc(100% + ${u(ICON_GUTTER)})`, gap: u(ICON_GUTTER), pointerEvents: 'auto' }}
      >
        <GlyphButton
          label={isKeyboardOpen ? 'Hide text input' : 'Type a message'}
          title={isKeyboardOpen ? 'Hide text input' : 'Type a message'}
          glyph={KEYBOARD_GLYPH}
          dimmed={!isKeyboardOpen}
          onClick={isKeyboardOpen ? onCloseKeyboard : onOpenKeyboard}
        />
        {showCamera && (
          <GlyphButton label="Share a photo of your own piece or watch" onClick={onCamera}>
            <Camera size={16} className="text-reel-chrome-textPrimary" />
          </GlyphButton>
        )}
        {/* Manual interrupt (SPEC-73). Rides beside the keyboard, and shows in voice mode as well as
            keyboard mode: voice barge-in is energy-triggered, so it is not dependable in a real
            room, and the guest needs one control that always cuts the agent short. With the
            keyboard open barge-in is withheld outright (App.js `useVoiceActivityDetection`, the
            rising-edge branch: a laptop's speakers would loop the agent's voice into an
            interrupt), so there it is the only way. The parent renders it for tenants with
            `manual_interrupt`; it has no slot in the Figma frames, the same documented departure
            as the camera (OQ-R5). No glyph exists in /reel-chrome for it, so it takes a lucide
            icon like the camera does. */}
        {showStop && (
          <GlyphButton
            label="Stop the agent"
            dimmed={!canStop}
            disabled={!canStop}
            onClick={onStopAgent}
          >
            <Square size={16} fill="currentColor" className="text-reel-chrome-textPrimary" />
          </GlyphButton>
        )}
      </div>
      )}

      {/* The bar itself — hides when there is nothing to frame, without moving the flanks. */}
      <div className="w-full h-full" style={{ pointerEvents: 'auto' }}>
        <CaptionOverlay.Bar
          enabled={ccEnabled}
          visualizer={voiceFirst?.dock ? null : visualizer}
          input={input}
          unit={CAPTION_BAR_UNIT}
          {...(voiceFirst ? { bare: voiceFirst.bare, clusterW: voiceFirst.dock ? dockSlotWidth('desktop', voiceFirst.ring) : undefined } : {})}
        />
      </div>

      {/* SPEC-82: the dock lives in the wrapper (not the clipping bar), at the cluster slot. */}
      {voiceFirst?.dock && (
        <VoiceDock
          composition="desktop"
          core={visualizer}
          ring={voiceFirst.ring}
          talk={voiceFirst.talk}
          reveal={voiceFirst.reveal}
          controls={buildDockControls({
            isKeyboardOpen, onOpenKeyboard, onCloseKeyboard, isMicOn, onToggleMic, showStop, canStop, onStopAgent, showCamera, onCamera,
          })}
          reducedMotion={voiceFirst.reducedMotion}
          connecting={voiceFirst.connecting || null} // 2026-10-08: 'Connecting to …' while the agent starts
        />
      )}

      {/* Right flank — the mic, outside the bar. */}
      {!voiceFirst?.hideFlanks && (
      <div
        className="absolute top-1/2 -translate-y-1/2"
        style={{ left: `calc(100% + ${u(ICON_GUTTER)})`, pointerEvents: 'auto' }}
      >
        <GlyphButton
          label={isMicOn ? 'Mute microphone' : 'Unmute microphone'}
          glyph={isMicOn ? MIC_GLYPH : MIC_GLYPH_OFF}
          dimmed={!isMicOn}
          onClick={onToggleMic}
        />
      </div>
      )}
    </div>
  );
}

export default ReelFooter;
